import { useQuery } from "@tanstack/react-query";
import { CheckCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { get, post } from "../lib/api";
import { cn } from "../lib/cn";
import { relTime } from "../lib/format";
import { useAction } from "../lib/hooks";
import type { NotificationRow } from "../lib/types";
import { Dot, Empty } from "./ui/primitives";

const CAT: Record<string, string> = {
  plan_pending: "Plan pendiente", delivery_pending: "Entrega pendiente", mission_blocked: "Misión bloqueada", agent_failed: "Agente fallido",
  ci_failed: "CI fallido", budget_exceeded: "Presupuesto", mission_done: "Misión finalizada", merge_completed: "Merge completado",
};

export function useNotifications() {
  return useQuery({ queryKey: ["notifications"], queryFn: () => get<NotificationRow[]>("/notifications"), refetchInterval: 10_000 });
}
/** Solo cuentan como pendientes las accionables sin resolver: evita spam de avisos informativos. */
export function useUnreadCount() {
  const q = useNotifications();
  return (q.data ?? []).filter((n) => !n.readAt && !n.resolvedAt && (n.actionable || n.severity !== "info")).length;
}

export function NotificationsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const q = useNotifications();
  const markAll = useAction(() => post("/notifications/read-all"), { invalidate: [["notifications"]] });
  const read = useAction((id: string) => post(`/notifications/${id}/read`), { invalidate: [["notifications"]] });
  if (!open) return null;
  const items = q.data ?? [];
  const actionable = items.filter((n) => n.actionable && !n.resolvedAt);
  const rest = items.filter((n) => !(n.actionable && !n.resolvedAt));
  const row = (n: NotificationRow) => {
    const body = (
      <div className={cn("flex gap-3 border-t border-ln px-4 py-3", n.readAt && "opacity-60")}>
        <Dot color={n.severity === "critical" ? "er" : n.severity === "warning" ? "wn" : "in"} className="mt-1.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold">{n.title}</div>
          {n.body && <div className="mt-0.5 truncate text-[13px] text-mu">{n.body}</div>}
          <div className="mt-1 font-mono text-[11px] text-mu">{CAT[n.category] ?? n.category} · {relTime(n.createdAt)}{n.resolvedAt ? " · resuelta" : ""}</div>
        </div>
      </div>
    );
    return n.missionId ? <Link key={n.id} to={`/missions/${n.missionId}`} onClick={() => { if (!n.readAt) read.mutate(n.id); onClose(); }} className="block hover:bg-hv">{body}</Link> : <div key={n.id}>{body}</div>;
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/55" onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()} className="slide-up flex h-full w-full max-w-[420px] flex-col border-l border-ln2 bg-sf" aria-label="Centro de notificaciones">
        <div className="safe-t flex items-center justify-between px-4 py-3">
          <h2 className="m-0 font-display text-[20px] font-medium">Notificaciones</h2>
          <div className="flex">
            <button onClick={() => markAll.mutate()} aria-label="Marcar todo como leído" title="Marcar todo como leído" className="flex h-11 w-11 cursor-pointer items-center justify-center text-mu hover:text-tx"><CheckCheck className="h-5 w-5" /></button>
            <button onClick={onClose} aria-label="Cerrar" className="flex h-11 w-11 cursor-pointer items-center justify-center text-mu hover:text-tx"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!items.length && <Empty>Sin notificaciones.</Empty>}
          {actionable.length > 0 && <div className="px-4 pb-1 pt-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ok">Requieren acción</div>}
          {actionable.map(row)}
          {rest.length > 0 && <div className="px-4 pb-1 pt-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-mu">Informativas</div>}
          {rest.map(row)}
        </div>
        <p className="m-0 border-t border-ln px-4 py-3 text-[12px] text-mu">Las aprobaciones pendientes siempre aparecen en «Requiere tu atención», con o sin notificaciones push.</p>
      </aside>
    </div>
  );
}
